import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Nav from "../../hooks/Nav";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { DataValue } from "./DataValue";
import Badge from "../../tailwindUI/Badge";
import Table from "../../tailwindUI/Table";
import PrimaryButton from "../../tailwindUI/PrimaryButton";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { FixatServiceSlideForm } from "./FixatServiceSlideForm";
import { useFixatServices } from "../../hooks/catalogs/useFixatServices";
import PermissionsGate from "../../cognito/permissions/PermissionsGate";
import { allowedRolesForRoutes } from "../../routes/SideBarRoutes";

const discountColumns = [
  { heading: "Cantidad mínima", value: "min_quantity" },
  { heading: "Cantidad máxima", value: "max_quantity" },
  { heading: "Descuento máximo", value: "max_discount" },
];

export function ViewFixatService() {
  const history = useHistory();
  const location = useLocation();

  const [service, setService] = useState(location.state.service || {});
  const [openSlideForm, setOpenSlideForm] = useState(false);

  const { formatService } = useFixatServices({});

  const editService = (serviceEdited) => {
    setService(formatService(serviceEdited));
  };

  return (
    <div className="h-full w-full">
      <div className="w-full">
        <Nav>
          <div className="text-white font-medium text-xl md:text-3xl xl:text-4xl flex gap-2 items-center">
            <ArrowLeftIcon
              className="w-10 md:w-12 h-10 md:h-12 cursor-pointer"
              onClick={() => history.goBack()}
            />
            Servicio Fixat
          </div>
        </Nav>
      </div>

      <div className="w-full px-4">
        <div className="md:w-full xs:hidden lg:block w-full">
          <div className="w-full text-3xl text-v2-input-text flex justify-between gap-4">
            <div className="w-full mt-4 flex items-center justify-between">
              <div className="text-gray-900 font-medium text-xl md:text-3xl xl:text-4xl flex gap-2 h-full">
                <ArrowLeftIcon
                  className="w-10 md:w-12 h-10 md:h-12 cursor-pointer"
                  onClick={() => history.goBack()}
                />
                <div className="h-full flex items-center">
                  <p>{service.name}</p>
                </div>
              </div>
              <div className="w-[12rem]">
                <PermissionsGate
                  allowedRoles={allowedRolesForRoutes.createServiceRoles}
                >
                  <PrimaryButton
                    isFullWidth={true}
                    onClick={() => setOpenSlideForm(true)}
                  >
                    Editar Servicio
                    <PencilSquareIcon className="h-5 w-5 ml-2" />
                  </PrimaryButton>
                </PermissionsGate>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:hidden xs:w-full md:w-full xs:mt-20 lg:mt-0 mb-4">
          <div className="w-full space-y-4">
            <div className="text-gray-900 text-xl md:text-3xl xl:text-4xl">
              <p>{service.name}</p>
              <p className="text-lg text-gray-700">SKU: {service.sku}</p>
            </div>
            <div className="w-full">
              <PermissionsGate
                allowedRoles={allowedRolesForRoutes.createServiceRoles}
              >
                <PrimaryButton
                  isFullWidth={true}
                  onClick={() => {
                    setOpenSlideForm(true);
                  }}
                >
                  Editar Servicio
                  <PencilSquareIcon className="h-5 w-5 ml-2" />
                </PrimaryButton>
              </PermissionsGate>
            </div>
          </div>
        </div>

        <div className="space-y-8 pt-4 lg:pt-8 pb-16">
          <section className="flex flex-col gap-y-4 pb-8 bg-white border border-gray-100 shadow-md rounded-lg">
            <header className="flex items-center h-[64px] px-6 border-b-2 border-gray-100 font-medium text-lg text-gray-900">
              <h3 className="flex-1">Servicio</h3>
            </header>
            <main className="px-6">
              <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                <div className="sm:col-span-full">
                  <h3 className="text-sm font-medium text-gray-500">
                    Descripción
                  </h3>
                  <p className="mt-1 text-sm text-gray-900 font-normal">
                    {service.description}
                  </p>
                </div>
                <DataValue
                  label="SKU"
                  value={service.sku}
                />
                <DataValue
                  label="Nombre corto"
                  value={service.short_name}
                />
                <DataValue
                  label="Precio"
                  value={`${service.price_formatted} ${service?.currency}`}
                />
                <DataValue
                  label="Tipo persona"
                  value={service.legal_type_formatted}
                />
                <DataValue
                  label="Periodicidad"
                  value={service.periodicity_formatted}
                />
                <DataValue
                  label="Categoría"
                  value={service.category_formatted}
                />
                <DataValue
                  label="Estatus"
                  value={<Badge text={service?.status} />}
                />
                <DataValue
                  label="Periodos de tiempo"
                  value={service.time_periods_formatted}
                />
                <DataValue
                  label="Periodos mínimos"
                  value={service.minimum_periods}
                />
                <DataValue label="Prioridad" value={service.priority} />
              </div>
            </main>
          </section>

          <section className="flex flex-col gap-y-4 pb-8 bg-white border border-gray-100 shadow-md rounded-lg">
            <header className="flex items-center h-[64px] px-6 border-b-2 border-gray-100 font-medium text-lg text-gray-900">
              <h3 className="flex-1">Descuentos</h3>
            </header>
            <main className="px-6 mt-4">
              <Table
                columns={discountColumns}
                data={service.tab_discount_formatted}
              />
            </main>
          </section>

          <section className="flex flex-col gap-y-4 pb-8 bg-white border border-gray-100 shadow-md rounded-lg">
            <header className="flex items-center h-[64px] px-6 border-b-2 border-gray-100 font-medium text-lg text-gray-900">
              <h3 className="flex-1">Alcance del servicio</h3>
            </header>
            <main className="px-6">
              <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                {service.service_info.service_reach?.length > 0 ? (
                  <div
                    className="list-container"
                    dangerouslySetInnerHTML={{
                      __html: service.service_reach,
                    }}
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-500">
                    Sin alcance del servicio
                  </p>
                )}
              </div>
            </main>
          </section>

          <section className="flex flex-col gap-y-4 pb-8 bg-white border border-gray-100 shadow-md rounded-lg">
            <header className="flex items-center h-[64px] px-6 border-b-2 border-gray-100 font-medium text-lg text-gray-900">
              <h3 className="flex-1">Términos y condiciones</h3>
            </header>
            <main className="px-6">
              <div className="w-full">
                {service.service_info.terms_and_conditions?.length > 0 ? (
                  <div
                    className="list-container"
                    dangerouslySetInnerHTML={{
                      __html: service.terms_and_conditions,
                    }}
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-500">
                    Sin terminos o condiciones
                  </p>
                )}
              </div>
            </main>
          </section>

          <section className="flex flex-col gap-y-4 pb-8 bg-white border border-gray-100 shadow-md rounded-lg">
            <header className="flex items-center h-[64px] px-6 border-b-2 border-gray-100 font-medium text-lg text-gray-900">
              <h3 className="flex-1">Cláusulas de cancelación</h3>
            </header>
            <main className="px-6">
              <div className="">
                {service.service_info.cancelation_clauses?.length > 0 ? (
                  <div
                    className="list-container"
                    dangerouslySetInnerHTML={{
                      __html: service.cancelation_clauses,
                    }}
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-500">
                    Sin cláusulas de cancelación
                  </p>
                )}
              </div>
            </main>
          </section>
        </div>
      </div>
      <FixatServiceSlideForm
        openSlideForm={openSlideForm}
        setOpenSlideForm={setOpenSlideForm}
        serviceToEdit={service}
        editService={editService}
      />
    </div>
  );
}
